import { Injectable, inject, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { getToken, onMessage, Messaging, isSupported, deleteToken } from '@angular/fire/messaging';
import { environment } from '../../environments/environment';
import { AppSnackService } from './snack-service.service';
import { LocalDatabaseService } from './local-database.service';
import { FCMMessage } from '../data/models/notifications.model';
import { Observable, from, of, switchMap, map, catchError } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private readonly messaging = inject(Messaging);
  private readonly snackSrv = inject(AppSnackService);
  private readonly localDbSrv = inject(LocalDatabaseService);
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
    private readonly http = inject(HttpClient);

  /**
   * Inicializa la lógica principal de Firebase Cloud Messaging (FCM).
   * 
   */
  public initializeFirebaseMessaging(): Observable<void> {
    return from(isSupported()).pipe(
      switchMap((supported) => {
        if (!supported) {
          this.snackSrv.openWithContext(
            'Firebase Messaging no es compatible con este navegador. No se recibirán notificaciones.',
            'warning'
          );
          return of(void 0);
        }

        // Se utiliza runInInjectionContext para proporcionar explícitamente el inyector de dependencias.
        // Esto es necesario porque funciones como onMessage (o utilidades internas de AngularFire)
        // pueden intentar usar inject() fuera del contexto de inicialización (constructor/campos).
        // Al estar dentro de un callback asíncrono, ese contexto se ha perdido, por lo que debemos restaurarlo manualmente.
        runInInjectionContext(this.injector, () => {
          onMessage(this.messaging, (payload: FCMMessage) => {
            this.ngZone.run(() => {
              this.snackSrv.openWithContext(
                `${payload.data?.title} - ${payload.data?.message}` || 'Mensaje de notificación',
                'notification'
              );
            });
          });
        });

        return this.requestPermission();
      })
    );
  }

  /**
   * Solicita permiso al usuario para recibir notificaciones push.
   * 
   */
  private requestPermission(): Observable<void> {
    return from(Notification.requestPermission()).pipe(
      switchMap((permission) => {
        if (permission !== 'granted') {
          return of(void 0);
        }
        return from(
          // Al igual que con onMessage, getToken puede requerir un contexto de inyección activo
          // para resolver dependencias internas. Al ejecutarse dentro de un flujo asíncrono,
          // el contexto original de inyección no está disponible.
          runInInjectionContext(this.injector, () =>
            getToken(this.messaging, { vapidKey: environment.firebaseConfig.vapidKey })
          )
        ).pipe(
          switchMap((currentToken) => {
            if (currentToken) {
              return from(this.localDbSrv.saveSetting('fcmToken', currentToken)).pipe(
                switchMap(() => this.saveFCMtoken(currentToken)),
                map(() => void 0)
              );
            } else {
              //console.log('No se pudo obtener el token de registro de FCM.');
              return of(void 0);
            }
          })
        );
      }),
      catchError((err) => {
        console.error('Ocurrió un error al solicitar el permiso o al obtener el token:', err);
        return of(void 0);
      })
    );
  }

    /**
   * Elimina el token de registro actual de FCM.
   * Esto permite que se pueda solicitar y generar uno nuevo posteriormente.
   */
  public removeToken(): void {
    deleteToken(this.messaging).then(() => {
      //console.log('Token eliminado, se puede solicitar uno nuevo');
    });
  }



  /**
   * Registra el token de fcm en la base para posteriores envíos desde el backend
   * 
   * @param token 
   * @returns 
   */
  private saveFCMtoken(token: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/notifications/register-token`, { fcmToken: token });
  }
}
